import { useState } from "react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Share2, Link as LinkIcon, Mail, MessageCircle, Facebook, Linkedin, Check } from "lucide-react";

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.244 2H21.5l-7.5 8.57L22.8 22h-6.9l-5.4-7.06L4.3 22H1.04l8.02-9.17L1.2 2h7.08l4.88 6.45L18.24 2Zm-1.2 18h1.8L7.02 3.9H5.1L17.04 20Z" />
  </svg>
);

function targets(url, text) {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(text);
  return [
    { id: "x", label: "X", icon: XIcon, href: `https://twitter.com/intent/tweet?url=${u}&text=${t}` },
    { id: "facebook", label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    { id: "linkedin", label: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${t}%20${u}` },
    { id: "email", label: "Email", icon: Mail, href: `mailto:?subject=${t}&body=${t}%0A%0A${u}` },
  ];
}

export function ShareMenu({ url, text, testId, compact = false }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          aria-label="Share"
          className={`inline-flex items-center gap-2 text-sm font-medium text-[#A8A29E] hover:text-[#D4AF37] transition-colors ${compact ? "p-2 border border-[#D4AF37]/30 bg-[#0A0A0A]/80 hover:border-[#D4AF37]" : ""}`}
        >
          <Share2 className="w-4 h-4" /> {!compact && "Share"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2 bg-[#141414] border-[#D4AF37]/30 rounded-none" data-testid={testId ? `${testId}-menu` : undefined}>
        <div className="flex items-center gap-1">
          {targets(shareUrl, text).map((s) => (
            <a
              key={s.id}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              title={s.label}
              aria-label={`Share on ${s.label}`}
              data-testid={testId ? `${testId}-${s.id}` : undefined}
              className="p-2.5 text-[#F5F3EF] hover:text-[#0A0A0A] hover:bg-[#D4AF37] transition-colors"
            >
              <s.icon className="w-4 h-4" />
            </a>
          ))}
          <button
            type="button"
            onClick={copy}
            title="Copy link"
            aria-label="Copy link"
            data-testid={testId ? `${testId}-copy` : undefined}
            className="p-2.5 text-[#F5F3EF] hover:text-[#0A0A0A] hover:bg-[#D4AF37] transition-colors"
          >
            {copied ? <Check className="w-4 h-4 text-[#5DBB7A]" /> : <LinkIcon className="w-4 h-4" />}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
